import React, { useEffect, useState } from "react";
import { MapContainer, GeoJSON, TileLayer } from "react-leaflet";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import "leaflet/dist/leaflet.css";

// Styled
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const formatNumber = (value) =>
  new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const RevenueByCountryChart = ({ salesData = [] }) => {
  const [geoData, setGeoData] = useState(null);
  const [countryRevenueData, setCountryRevenueData] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("00");
  const [availableYears, setAvailableYears] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGeoData = async () => {
      try {
        const response = await fetch(
          "https://raw.githubusercontent.com/leakyMirror/map-of-europe/master/GeoJSON/europe.geojson"
        );
        const data = await response.json();
        setGeoData(data);
      } catch {
        setError("Błąd ładowania danych mapy Europy.");
      }
    };
    fetchGeoData();
  }, []);

  useEffect(() => {
    if (salesData.length === 0) return;
    const years = [...new Set(salesData.map((row) => row.Rok))].sort();
    setAvailableYears(years);
    if (!selectedYear && years.length) {
      setSelectedYear(years[years.length - 1]); // latest
    }
  }, [salesData, selectedYear]);

  useEffect(() => {
    if (!selectedYear) return;

    const filtered = salesData.filter(
      (row) =>
        row.Rok === selectedYear &&
        (selectedMonth === "00" || row.Miesiac === parseInt(selectedMonth))
    );

    const grouped = filtered.reduce((acc, row) => {
      const revenue =
        parseFloat(row.K_15) ||
        parseFloat(row.K_19) ||
        parseFloat(row.K_10) ||
        parseFloat(row.K_21) ||
        parseFloat(row.K_17) ||
        parseFloat(row.K_11) ||
        parseFloat(row.K_22) ||
        0;
      const country = row.KodKrajuNadaniaTIN?.trim() || "Unknown";
      acc[country] = (acc[country] || 0) + revenue;
      return acc;
    }, {});

    const total = Object.values(grouped).reduce((sum, v) => sum + v, 0);

    const formatted = Object.keys(grouped)
      .filter((c) => c !== "OTHER")
      .map((c) => ({
        country: c,
        revenue: grouped[c],
        percentage: total ? ((grouped[c] / total) * 100).toFixed(2) : "0",
      }));

    setCountryRevenueData(formatted);
  }, [salesData, selectedYear, selectedMonth]);

  // ✅ Color scale: green good (high %), yellow mid, red = 1–9%, gray = 0%
  const styleFeature = (feature) => {
    const code = feature.properties.ISO2.trim();
    const data = countryRevenueData.find((c) => c.country === code);
    const pct = data ? parseFloat(data.percentage) : 0;

    const getColor = (p) => {
      if (p >= 50) return "#2e7d32";   // dark green (very high)
      if (p >= 25) return "#66bb6a";   // medium green
      if (p >= 10) return "#ffd54f";   // yellow
      if (p > 0)  return "#d32f2f";    // 1–9% -> red (bad)
      return "#cccccc";                // 0% -> gray (no share)
    };

    return {
      fillColor: getColor(pct),
      weight: 0.7,
      opacity: 0.4,
      color: "#444",
      fillOpacity: 0.3,
    };
  };

  const onEachFeature = (feature, layer) => {
    const code = feature.properties.ISO2.trim();
    const data = countryRevenueData.find((d) => d.country === code);
    const revenue = data ? `${formatNumber(data.revenue)}` : "Brak danych";
    const pct = data ? `${data.percentage}%` : "0%";

    layer.bindPopup(
      `<strong>Kraj:</strong> ${feature.properties.NAME}<br/>
       <strong>Przychód:</strong> ${revenue}<br/>
       <strong>Udział:</strong> ${pct}`
    );
  };

  const months = [
    ["00", "Cały rok"],
    ["01", "Styczeń"],
    ["02", "Luty"],
    ["03", "Marzec"],
    ["04", "Kwiecień"],
    ["05", "Maj"],
    ["06", "Czerwiec"],
    ["07", "Lipiec"],
    ["08", "Sierpień"],
    ["09", "Wrzesień"],
    ["10", "Październik"],
    ["11", "Listopad"],
    ["12", "Grudzień"],
  ];

  return (
    <Wrapper>
      <ChartTitle>
        Udział Przychodów wg Krajów
        <InfoIconWrapper
          data-tooltip-id="revenue-map-tooltip"
          data-tooltip-html="
            <strong>Mapa udziału przychodów</strong><br/>
            Kolor pokazuje udział kraju w całości przychodów.<br/>
            Kliknij kraj aby zobaczyć szczegóły."
        >
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip id="revenue-map-tooltip" place="left" />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            disabled={!availableYears.length}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      {!geoData || countryRevenueData.length === 0 ? (
        <p style={{ marginTop: "20px", color: "#888", textAlign: "center" }}>
          Brak danych do wyświetlenia.
        </p>
      ) : (
        <>
          <MapContainer
            style={{ height: "500px", width: "100%" }}
            center={[51.2, 15.2]}
            zoom={4.2}
            scrollWheelZoom={false}
            doubleClickZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <GeoJSON
              data={geoData}
              style={styleFeature}
              onEachFeature={onEachFeature}
            />
          </MapContainer>

          {/* Legend */}
          <div
            style={{
              marginTop: "24px",
              padding: "20px",
              maxWidth: "800px",
              marginLeft: "auto",
              marginRight: "auto",
              textAlign: "left",
            }}
          >
            <h4 style={{
              marginBottom: "18px",
              textAlign: "center",
              fontSize: "18px",
              fontWeight: 600,
              color: "#333"
            }}>
              Legenda mapy udziałów przychodów
            </h4>


            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              {[
                { color: "#2e7d32", label: "≥ 50%",     desc: "bardzo wysoki udział (dobry)" },
                { color: "#66bb6a", label: "25% - 49%", desc: "wysoki udział (dobry)" },
                { color: "#ffd54f", label: "10% - 24%", desc: "średni udział (neutralny)" },
                { color: "#d32f2f", label: "1% - 9%",   desc: "niski udział (zły)" },
                { color: "#cccccc", label: "0%",        desc: "brak udziału" },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "6px",
                      backgroundColor: item.color,
                      border: "1px solid #999",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                  <div style={{ lineHeight: "1.2" }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#333",
                      }}
                    >
                      {item.label}
                    </div>
                    <div style={{ fontSize: "12px", color: "#555" }}>
                      {item.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default RevenueByCountryChart;
